import React, { useState } from 'react';
import axios from 'axios';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import '../styles/Login.css';
import { Link, useNavigate } from 'react-router-dom';
import { API_URL } from '../config';

interface GoogleUser {
  email: string;
  name: string;
  given_name: string;
  family_name: string;
  picture?: string;
}

const api = axios.create({
  baseURL: API_URL, // Reemplazas el string harcodeado por la variable
  headers: {
    'Content-Type': 'application/json', // Recomendado para Django
  },
});
// (Opcional) agrega el token automáticamente si existe
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// -------- Modal para completar documento ----------
const DocumentModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}> = ({ open, onClose, onSaved }) => {
  const [numeroDocumento, setNumeroDocumento] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const validar = (v: string) => {
    const s = v.replace(/\./g, '').trim();
    if (!s) return 'Ingresa tu número de documento';
    if (s.length > 20) return 'Muy largo (máx. 20)';
    return '';
  };

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validar(numeroDocumento);
    if (err) return setError(err);
    setError('');
    setSaving(true);
    try {
      await api.patch('/api/perfil/numero-documento/', {
        numero_documento: numeroDocumento.trim(),
      });
      onSaved();
    } catch (e: any) {
      const msg =
        e?.response?.data?.numero_documento?.[0] ||
        e?.response?.data?.error ||
        'No se pudo guardar (verifica si ya está usado).';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card">
        <button className="modal-close" onClick={onClose} aria-label="Cerrar">×</button>
        <h3 className="modal-title">Completar perfil</h3>
        <p className="modal-subtitle">Necesitamos tu número de documento para continuar.</p>

        <form onSubmit={guardar} className="form-stacked">
          <label className="form-label">
            Número de documento
            <input
              className="input"
              type="text"
              value={numeroDocumento}
              onChange={(e) => setNumeroDocumento(e.target.value)}
              placeholder="Ej: 12345678-9"
              autoFocus
            />
          </label>

          {error && <div className="alert error">{error}</div>}

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar y continuar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
// --------------------------------------------------

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/api/token/', { username: email, password });
      localStorage.setItem('access_token', res.data.access);
      localStorage.setItem('refresh_token', res.data.refresh);
      navigate('/carrito');
    } catch {
      setMessage('Credenciales inválidas');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    if (!credentialResponse.credential) {
      setMessage('No llegaron las credenciales de Google');
      return;
    }

    const decoded = jwtDecode<GoogleUser>(credentialResponse.credential);
    const userEmail = decoded.email;
    const nombre = decoded.name?.split(' ')[0] || '';
    const apellido = decoded.name?.split(' ').slice(1).join(' ') || '';
    const carrito = JSON.parse(localStorage.getItem('carrito') || '[]');

    setMessage(`Hola ${decoded.name}, iniciando sesión...`);
    setLoading(true);

    try {
      const { data } = await api.post('/api/login-google/', {
        email: userEmail,
        nombre,
        apellido,
        carrito,
      });

      localStorage.setItem('access_token', data.access);
      localStorage.setItem('refresh_token', data.refresh);

      if (data.perfil_incompleto) {
        setShowDocModal(true);
        setMessage('Completa tu número de documento para continuar.');
      } else {
        navigate('/carrito');
      }
    } catch {
      setMessage('Error al iniciar sesión con Google ❌');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setMessage('Error al iniciar sesión con Google ❌');
  };

  const handleDocSaved = () => {
    setShowDocModal(false);
    setMessage('Perfil completado ✅');
    navigate('/carrito');
  };

  return (
    <div className="auth-wrapper">
      <div className="card">
        <h2 className="title">Iniciar sesión</h2>

        {message && <div className="alert">{message}</div>}

        <form onSubmit={handleSubmit} className="form-stacked">
          <label className="form-label">
            Correo electrónico
            <input
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </label>

          <label className="form-label">
            Contraseña
            <input
              className="input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </label>

          <button className="btn btn-primary w-full" type="submit" disabled={loading}>
            {loading ? 'Entrando…' : 'Entrar'}
          </button>
        </form>

        <div className="separator"><span>o</span></div>

        <div className="google-login">
          <GoogleLogin onSuccess={handleGoogleSuccess} onError={handleGoogleError} />
        </div>

        <div className="links">
          <Link to="/forgot-password">¿Olvidaste tu clave?</Link>
          <span>·</span>
          <Link to="/register">Registrarse</Link>
        </div>
      </div>

      {/* Modal para documento */}
      <DocumentModal
        open={showDocModal}
        onClose={() => setShowDocModal(false)}
        onSaved={handleDocSaved}
      />
    </div>
  );
};

export default Login;
